/**
 * The localStorage key the app's Supabase auth client persists its session
 * under. A dependency-free leaf so auth helpers can read THIS client's session
 * without importing (or depending on internals of) the client itself.
 *
 * Loopback stacks get a host+port-scoped key so parallel local stacks never
 * share a session; deployed hosts use supabase-js's own default,
 * `sb-<first hostname label>-auth-token` (supabase-js 2.x `defaultStorageKey`).
 */
function isLoopbackSupabaseHost(hostname: string): boolean {
  return hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "::1";
}

/** The custom key for a loopback stack; undefined means "use the supabase-js default". */
export function getSupabaseAuthStorageKey(supabaseUrl: string): string | undefined {
  try {
    const parsed = new URL(supabaseUrl);
    if (!isLoopbackSupabaseHost(parsed.hostname)) {
      return undefined;
    }

    const host = parsed.hostname.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "");
    const port = parsed.port || (parsed.protocol === "https:" ? "443" : "80");
    return `sb-${host}-${port}-auth-token`;
  } catch {
    return undefined;
  }
}

/** The key the client actually uses (custom or default); undefined when the URL is unusable. */
export function resolveSupabaseAuthStorageKey(supabaseUrl: string): string | undefined {
  const custom = getSupabaseAuthStorageKey(supabaseUrl);
  if (custom) {
    return custom;
  }
  try {
    return `sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`;
  } catch {
    return undefined;
  }
}

export const SUPABASE_AUTH_STORAGE_KEY = resolveSupabaseAuthStorageKey(
  import.meta.env.VITE_SUPABASE_URL ?? "",
);
